import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import React, {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

interface ConfirmDialogProps {
  title: string;
  description: string;
  setResult?: (value: boolean) => void;
  destructive?: boolean;
}

// Define the type for the ref object
export interface ConfirmDialogRef {
  openDialog: () => void;
}

const ConfirmDialog = memo(
  forwardRef<ConfirmDialogRef, ConfirmDialogProps>(
    ({ title, description, setResult, destructive }, ref) => {
      const { isOpen, onOpen, onClose } = useDisclosure();

      const handleCancel = useCallback(() => {
        onClose();
        setResult?.(false);
      }, [onClose, setResult]);

      const handleAccept = useCallback(() => {
        onClose();
        setResult?.(true);
      }, [onClose, setResult]);

      const openDialog = useCallback(() => {
        onOpen();
      }, [onOpen]);

      useImperativeHandle(ref, () => ({
        openDialog,
      }));

      return (
        <Modal
          isOpen={isOpen}
          onClose={handleCancel}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader id="alert-dialog-title">{title}</ModalHeader>
            <ModalCloseButton />
            <ModalBody gap={4}>
              <Flex gap={2} direction={"column"}>
                <Text>{description}</Text>
              </Flex>
            </ModalBody>
            <ModalFooter gap={2}>
              <Button
                onClick={handleCancel}
                borderColor={destructive ? "red.500" : "brand.500"}
                variant="outline"
                borderRadius={8}
                color={destructive ? "red.500" : "brand.500"}
              >
                Cancel
              </Button>
              <Button
                bgColor={destructive ? "red.500" : "brand.500"}
                color={"white"}
                borderRadius={8}
                _hover={{
                  backgroundColor: destructive ? "red.600" : "brand.600",
                }}
                onClick={handleAccept}
                autoFocus
              >
                Confirm
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      );
    }
  )
);

export default ConfirmDialog;

export const useConfirmDialog = (props: ConfirmDialogProps) => {
  const ref = React.useRef<ConfirmDialogRef>(null);
  const [resolveResult, setResolveResult] =
    useState<(value: boolean) => void>();

  const openDialog = useCallback(() => {
    return new Promise<boolean>((resolve) => {
      setResolveResult(() => resolve); // Store the resolver function
      ref.current?.openDialog();
    });
  }, []);

  const ConfirmDialogComponent = useMemo(() => {
    return (
      <ConfirmDialog
        {...props}
        ref={ref}
        setResult={(value) => {
          resolveResult?.(value); // Resolve the promise with the value
        }}
      />
    );
  }, [props, resolveResult]);

  return {
    ConfirmDialog: ConfirmDialogComponent,
    openDialog,
  };
};
